import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  BLOCKED_ORDER_BUCKETS,
  CLR_KINDS,
  INBOX_MODES,
  INBOX_SCOPES,
  INBOX_SORTS,
  INBOX_STAGES,
  INBOX_TASK_TYPES,
  isBlockedOrderDecision,
  type BlockedOrderBucket,
  type ClrDecision,
  type ClrKind,
  type InboxMode,
  type InboxScope,
  type InboxSort,
  type InboxStage,
  type InboxTaskType,
} from '@repo/constants'
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { extractBlockedOrderErrorType } from './useBlockedOrders'
import { extractClrErrorType } from './useCreditLimitReviews'
import { extractMassClrErrorType } from './useMassClr'

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The reviewer/approver inbox (FUZE-616), over /credit/inbox.
//
// No hook here checks `res.ok`: the hono client is constructed with `fetch: fetchWithThrow`
// (see stores/api.ts), which throws the parsed error body on any non-2xx before `res.json()` is
// reached. That body carries the `issues` a `clr.errors.<key>` toast is translated from, so adding
// a local check would at best be dead and at worst swallow it. Response types are inferred from the
// hono router, like every other credit hook — the feed's discriminated union comes across intact,
// so the detail pane can switch on `task.type` with no local type declarations.
// ─────────────────────────────────────────────────────────────────────────────────────────────

export { CLR_KINDS, INBOX_SCOPES, INBOX_SORTS, INBOX_STAGES, INBOX_TASK_TYPES }

export const inboxSearchSchema = z.object({
  /**
   * Which queue is showing. Absent from a fresh URL, where the page falls back to the last mode the
   * reader used (or the one a deep-linked task belongs to).
   */
  mode: z.enum(INBOX_MODES).optional(),
  /** The blocked-orders mode's tabs; the CLR mode's equivalent is `stage`. */
  bucket: z.enum(BLOCKED_ORDER_BUCKETS).optional(),
  stage: z.enum(INBOX_STAGES).optional(),
  type: z.enum(INBOX_TASK_TYPES).optional(),
  kind: z.array(z.enum(CLR_KINDS)).optional(),
  scope: z.enum(INBOX_SCOPES).optional(),
  sort: z.enum(INBOX_SORTS).optional(),
  keyword: z.string().optional(),
  /** Which task is open in the detail pane, so a reload and a shared link land in the same place. */
  taskType: z.enum(INBOX_TASK_TYPES).optional(),
  /** A blocked order's SAP case id, or another task's numeric id. */
  taskId: z.union([z.number(), z.string()]).optional(),
})
export type InboxSearch = z.infer<typeof inboxSearchSchema>

export type InboxQuery = {
  mode?: InboxMode
  bucket?: BlockedOrderBucket
  stage?: InboxStage
  type?: InboxTaskType
  kind?: ClrKind[]
  scope?: InboxScope
  sort?: InboxSort
  keyword?: string
  page?: number
  pageSize?: number
}

async function fetchInbox(query: InboxQuery) {
  const res = await getApi().credit.inbox.$get({ query: stringifyValues(query) })
  const feed = await res.json()

  // Timestamps cross the wire as ISO strings; every consumer wants Dates, so they are converted
  // once here rather than at each call site.
  return {
    ...feed,
    tasks: feed.tasks.map((task) => {
      const dates = {
        raisedAt: new Date(task.raisedAt),
        pendingSince: new Date(task.pendingSince),
        // Null on anything still in the chain, including a request awaiting other approvers.
        resolvedAt: task.resolvedAt ? new Date(task.resolvedAt) : null,
      }
      // Blocked orders also carry cutoff instants, the batch's and each of its orders'. A branch
      // rather than a conditional spread, which would type every task's `orders` as either shape.
      if (task.type === 'blocked_orders') {
        return {
          ...task,
          ...dates,
          cutoffAt: task.cutoffAt ? new Date(task.cutoffAt) : null,
          createdAtSap: task.createdAtSap ? new Date(task.createdAtSap) : null,
          orders: task.orders.map((order) => ({
            ...order,
            cutoffAt: order.cutoffAt ? new Date(order.cutoffAt) : null,
          })),
        }
      }
      return { ...task, ...dates }
    }),
  }
}

export const inboxQueryOptions = (query: InboxQuery) =>
  queryOptions({
    queryKey: ['credit', 'inbox', query],
    queryFn: () => fetchInbox(query),
    // The feed is a work queue: keeping the previous page visible while a filter change resolves
    // stops the list from blanking under the reader mid-triage.
    placeholderData: keepPreviousData,
    // A blocked order changes bucket as its cutoff approaches, and the server decides the bucket.
    // Without a refetch a row could count down to 45m while still filed under Medium.
    refetchInterval: query.mode === 'blocked_orders' ? 60_000 : false,
  })

export type InboxFeed = Awaited<ReturnType<typeof fetchInbox>>
export type InboxTask = InboxFeed['tasks'][number]
export type SingleClrInboxTask = Extract<InboxTask, { type: 'single_clr' }>
export type MassClrInboxTask = Extract<InboxTask, { type: 'mass_clr' }>
export type InitiativeInboxTask = Extract<InboxTask, { type: 'initiative' }>
export type BlockedOrderInboxTask = Extract<InboxTask, { type: 'blocked_orders' }>

export function useInbox(query: InboxQuery) {
  return useQuery(inboxQueryOptions(query))
}

/**
 * Marks a task read. Optimistic: opening a row should feel instantaneous, and the worst case of a
 * failed write is a row that stays bold until the next refetch — no data is at risk.
 */
export function useMarkInboxTaskRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (task: { type: InboxTaskType; id: number | string }) => {
      const res = await getApi().credit.inbox[':type'][':id'].read.$put({
        param: { type: task.type, id: String(task.id) },
      })
      return res.json()
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] }),
  })
}

/**
 * A reviewer/approver decision from the inbox, dispatched to whichever endpoint owns the task.
 * Both flows take the same verbs and both require a comment, so the detail pane needs one mutation
 * rather than a branch per type — and both raise the same `clr.errors.<key>` business-rule keys,
 * so one `onError` translates for either.
 */
export function useInboxDecision() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: {
      task: { type: InboxTaskType; id: number | string }
      action: ClrDecision
      comment: string
    }) => {
      const api = getApi()
      const param = { id: String(input.task.id) }
      const json = { action: input.action, comment: input.comment }

      // All four endpoints take the same body — that is deliberate, so this stays a dispatch
      // rather than three different call shapes.
      if (input.task.type === 'blocked_orders') {
        // No send-back on a blocked order — the footer never offers it, and the endpoint's schema
        // would refuse it. Narrowed here so the request type matches what the endpoint accepts.
        const action = input.action
        if (!isBlockedOrderDecision(action)) throw new Error(`Unsupported blocked-order decision`)
        const res = await api.credit['blocked-orders'].reviews[':id'].actions.$post({
          param,
          json: { action, comment: input.comment },
        })
        return await res.json()
      }

      const res =
        input.task.type === 'mass_clr'
          ? await api.credit['mass-credit-limit-reviews'][':id'].actions.$post({ param, json })
          : input.task.type === 'initiative'
            ? await api.credit.initiatives[':id'].actions.$post({ param, json })
            : await api.credit['credit-limit-reviews'][':id'].actions.$post({ param, json })

      return await res.json()
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      // The per-type lists and the customer page's eligibility both move on a terminal outcome.
      queryClient.invalidateQueries({ queryKey: ['creditLimitReviews'] })
      queryClient.invalidateQueries({ queryKey: ['massClrRuns'] })
      // Prefix matches (no id): the run page hosts decisions now, and its detail, lines and
      // comment threads all change state on one — a stale pane here reads as a lost decision.
      queryClient.invalidateQueries({ queryKey: ['massClrRun'] })
      queryClient.invalidateQueries({ queryKey: ['massClrRunLines'] })
      queryClient.invalidateQueries({ queryKey: ['massClrLineComments'] })
      queryClient.invalidateQueries({ queryKey: ['clrEligibility'] })
      // Initiative decisions move the worksheet and the list alike (prefix matches, no id).
      queryClient.invalidateQueries({ queryKey: ['initiatives'] })
      queryClient.invalidateQueries({ queryKey: ['initiative'] })
      queryClient.invalidateQueries({ queryKey: ['initiativePayerRows'] })
      // Prefix match: the blocked-order detail pane's lines and history both move on a decision.
      queryClient.invalidateQueries({ queryKey: ['blockedOrderReview'] })
      // A released or rejected order now has a snapshot, and the cockpit row moves status.
      queryClient.invalidateQueries({ queryKey: ['blockedOrderResolutionSnapshot'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrders'] })
      // The standalone review page stays where it is after a decision, so its own two queries have
      // to move with it: the task drives the resolved banner and whether the footer is there at
      // all, and the detail carries the `viewer` the page reads its authority from. Neither is a
      // prefix of the plural list key above.
      queryClient.invalidateQueries({ queryKey: ['singleClrTask'] })
      queryClient.invalidateQueries({ queryKey: ['creditLimitReview'] })

      // Mass runs and initiatives are unanimous, so an approval can succeed WITHOUT moving the
      // request: the vote is recorded and the status stays "Pending Approver" until the last
      // approver signs. A blanket "Decision saved" there reads as a failed approval, which is
      // exactly how this looked in UAT. Both responses carry the counts, so the task type — not the
      // payload shape — decides which copy applies: the terminal mass copy (run approved, SAP
      // hand-off pending) is about a run, and must never fire for an initiative.
      const hasVoteCounts = 'approvedCount' in data && 'requiredApproverCount' in data
      if (hasVoteCounts && variables.action === 'advance' && variables.task.type === 'initiative') {
        // Same partial-vote test as the worksheet hook; a completed approval falls through to the
        // generic "Decision saved" below.
        const isPartial =
          'actionType' in data &&
          data.actionType === 'approved' &&
          data.approvedCount < data.requiredApproverCount
        if (isPartial) {
          toast.success(
            t('initiatives.toasts.approvalRecorded', {
              approved: data.approvedCount,
              required: data.requiredApproverCount,
            }),
          )
          return
        }
      }
      if (hasVoteCounts && variables.action === 'advance' && variables.task.type === 'mass_clr') {
        const isPartial =
          data.requiredApproverCount > 1 &&
          data.status === 'in_review' &&
          data.pendingSlot === 'approver'
        if (isPartial) {
          toast.success(
            t('massClr.toasts.approvalRecorded', {
              approved: data.approvedCount,
              count: data.requiredApproverCount,
            }),
          )
          return
        }
        if (data.status === 'approved') {
          toast.success(t('massClr.toasts.approvedFinal'))
          return
        }
      }

      toast.success(t('clr.toasts.actionSuccess'))
    },
    onError: (error, variables) => {
      // Mass decisions raise mass-specific rule errors (reviewer gates, exclusion rules) that
      // the single-CLR guard does not recognize — fall back so they translate too.
      const errorType =
        extractClrErrorType(error) ??
        extractMassClrErrorType(error) ??
        extractBlockedOrderErrorType(error)
      toast.error(errorType ? t(`clr.errors.${errorType}`) : t('clr.toasts.error'))

      // A refused blocked-order decision means the page no longer matches the record — SAP moved
      // the payer to another band, someone else acted, or the viewer cannot act at its new step.
      // Reload what the page shows, so the next click is made on the record as it is now.
      if (errorType && variables.task.type === 'blocked_orders') {
        for (const queryKey of [['credit', 'inbox'], ['blockedOrderReview'], ['blockedOrders']]) {
          queryClient.invalidateQueries({ queryKey })
        }
      }
    },
  })
}
